import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, Send } from "lucide-react";

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BASE, authHeader } from "./shared";
import { PdfAttachmentField } from "./PdfAttachmentField";

export function CandidateEmailDialog({ open, onClose, candidateId, candidate, contact, pw, currentUser, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [senders, setSenders] = useState([]);
  const [sender, setSender] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const headers = useMemo(() => authHeader(pw), [pw]);

  useEffect(() => {
    if (!open) return;
    setSubject(""); setBody(""); setTemplateId(""); setTemplateName(""); setAttachment(null);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/outreach/custom-templates`, headers).then(r => setTemplates(r.data.templates || [])).catch(() => setTemplates([]));
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/senders`, headers).then(r => {
      const list = r.data.senders || []; setSenders(list);
      const me = list.find(item => item.email === (currentUser?.email || "").toLowerCase());
      setSender(me?.email || list[0]?.email || "");
    }).catch(() => setSenders([]));
  }, [open, headers, currentUser?.email]);

  const discardAndClose = async () => {
    if (attachment?.id) {
      try { await axios.delete(`${BASE}/email-attachments/${attachment.id}`, { headers: { "X-Admin-Password": pw } }); } catch (_) {}
    }
    setAttachment(null); onClose();
  };
  const chooseTemplate = (id) => {
    setTemplateId(id); const selected = templates.find(item => item.id === id);
    if (selected) { setTemplateName(selected.name || ""); setSubject(selected.subject || ""); setBody(selected.body || ""); }
  };
  const preview = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/outreach/preview`, {
        subject, body, brand: "careers", context: {
          first_name: contact?.first_name || "there", last_name: contact?.last_name || "",
          email: contact?.email || "", role: candidate?.role_focus || candidate?.headline || "",
          location: candidate?.location_now || "",
        },
      }, headers);
      setPreviewHtml(response.data.html || ""); setPreviewOpen(true);
    } catch (error) { toast.error(error?.response?.data?.detail || "Preview failed"); }
  };
  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body required."); return; }
    setBusy(true);
    try {
      const response = await axios.post(`${BASE}/candidates/${candidateId}/send-branded-email`, {
        subject, body, template_id: templateId || null, template_name: templateName || null,
        brand: "careers", sender_admin_email: sender || null, attachment_id: attachment?.id || null,
      }, headers);
      if (!response.data.ok) throw new Error(response.data?.resend?.error || "Send failed");
      const filename = response.data?.attached_document?.filename;
      setAttachment(null); toast.success(`Email sent${filename ? ` with ${filename}` : ""} + logged to timeline.`); onSent?.();
    } catch (error) { toast.error(error?.response?.data?.detail || error.message || "Send failed"); }
    finally { setBusy(false); }
  };

  return <>
    <Dialog open={open} onOpenChange={value => !value && discardAndClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="candidate-send-dialog">
        <DialogHeader><DialogTitle>Send branded email to {contact?.first_name || contact?.email || "candidate"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Select value={sender || "__none__"} onValueChange={value => setSender(value === "__none__" ? "" : value)}><SelectTrigger data-testid="candidate-send-sender"><SelectValue placeholder="Team desk"/></SelectTrigger><SelectContent><SelectItem value="__none__">Team desk (shared)</SelectItem>{senders.map(item => <SelectItem key={item.email} value={item.email}>{item.name} · {item.email}</SelectItem>)}</SelectContent></Select>
          <Select value={templateId || "__none__"} onValueChange={value => value === "__none__" ? setTemplateId("") : chooseTemplate(value)}><SelectTrigger data-testid="candidate-send-template"><SelectValue placeholder="Load a custom template (optional)"/></SelectTrigger><SelectContent><SelectItem value="__none__">Write from scratch</SelectItem>{templates.map(item => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent></Select>
          <input value={subject} onChange={event => setSubject(event.target.value)} data-testid="candidate-send-subject" placeholder="Email subject" className="w-full rounded-md border border-[#D6DCE3] px-3 py-2 text-sm"/>
          <textarea value={body} onChange={event => setBody(event.target.value)} data-testid="candidate-send-body" rows={10} placeholder="Hi {{first_name}}," className="w-full rounded-md border border-[#D6DCE3] px-3 py-2 text-sm font-mono"/>
          <PdfAttachmentField value={attachment} onChange={setAttachment} pw={pw} testIdPrefix="candidate-send"/>
        </div>
        <DialogFooter><Button variant="outline" onClick={discardAndClose} data-testid="candidate-send-cancel">Cancel</Button><Button variant="outline" onClick={preview} disabled={!subject || !body} data-testid="candidate-send-preview"><Eye className="mr-1 h-4 w-4"/>Preview</Button><Button onClick={send} disabled={busy || !subject || !body} data-testid="candidate-send-submit" className="bg-[#0D2F4D] text-[#C6D21F]"><Send className="mr-1 h-4 w-4"/>{busy ? "Sending…" : "Send email"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}><DialogContent className="max-w-4xl h-[92vh] flex flex-col p-0" data-testid="candidate-send-preview-dialog"><DialogHeader className="px-6 py-4 border-b"><DialogTitle>Branded preview — Careers</DialogTitle></DialogHeader><div className="flex-1 overflow-hidden bg-[#F2F2EF] p-4"><iframe title="Candidate email preview" srcDoc={previewHtml} className="h-full w-full bg-white" sandbox=""/></div></DialogContent></Dialog>
  </>;
}